'use client';

import ModalBackground from './ModalBackground';
import ModalCloseButton from './ModalCloseButton';
import ModalContent from './ModalContent';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';
import Portal from '../Portal';
import { cn } from '~/utils';
import { motion } from 'motion/react';
import useBlockBodyScrolling from '../hooks/useBlockBodyScrolling';
import { useEventListener } from '../hooks/DOM';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

export default function Modal(props: {
    className?: string;
    wrapperClassName?: string;
    onClose?:
        | 'router.back'
        | (() => void)
        // TODO(Louis): we should create a centralised module for this
        // types, utility functions for parsing, etc.
        | (Pick<Partial<URL>, 'pathname' | 'search'> & {
              searchParams: ConstructorParameters<typeof URLSearchParams>[0];
          });
    children?: React.ReactNode;
}) {
    const router = useRouter();

    function onClose() {
        if (props.onClose === 'router.back') {
            router.back();
        } else if (typeof props.onClose === 'function') {
            props.onClose();
        } else if (props.onClose) {
            const url = new URL(window.location.href);
            if (props.onClose.pathname) url.pathname = props.onClose.pathname;
            if (props.onClose.search) url.search = props.onClose.search;
            if (props.onClose.searchParams) url.search = new URLSearchParams(props.onClose.searchParams).toString(); // prettier-ignore

            router.push(url.toString());
        }
    }

    useEventListener('window', 'keydown', e => {
        if (e.key === 'Escape') onClose();
    });
    useBlockBodyScrolling();

    return (
        <Portal querySelector="#modals">
            {/* This background is not clickable and is not animated */}
            <ModalBackground onClick={onClose} className="bg-blue/80" />
            <motion.dialog
                open
                data-testid="Modal"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                aria-modal="true"
                tabIndex={-1}
                className={cn(
                    // Undo the user-agent dialog box (fit-content size, inline insets, canvas colours).
                    'h-auto max-h-none max-w-none bg-transparent text-inherit',
                    'z-modal pointer-events-none fixed inset-0 bottom-0 flex w-full items-center overflow-y-auto p-4 sm:p-0',
                    props.wrapperClassName,
                )}
            >
                <div
                    className={cn(
                        'pointer-events-auto relative mx-auto my-0 flex max-h-full flex-col rounded-xl bg-white sm:rounded-xl',
                        props.className,
                    )}
                >
                    {props.children}
                </div>
            </motion.dialog>
        </Portal>
    );
}

export function useModalState(isInitialOpen?: boolean | (() => boolean)) {
    const [isOpen, setIsOpen] = useState(isInitialOpen ?? false);

    return {
        isOpen,
        setIsOpen,
        toggle: () => setIsOpen(prev => !prev),
        close: () => setIsOpen(false),
        open: () => setIsOpen(true),
    } as const;
}

Modal.Background = ModalBackground;
Modal.CloseButton = ModalCloseButton;
Modal.Footer = ModalFooter;
Modal.Header = ModalHeader;
Modal.Content = ModalContent;
Modal.useState = useModalState;
